import React, { useState } from "react";
import Icon from "../../../../components/Icon";
import Modal from "../../../../components/Modal";
import { createEntry, updateEntry } from "../../services/KBService";

export function KBModal({ entry, onClose, onSaved }) {
  const isEdit = !!entry?.id;
  const [form, setForm] = useState({
    title: entry?.title || "",
    issue_type: entry?.issue_type || "",
    description: entry?.description || "",
    root_cause: entry?.root_cause || "",
    resolution: entry?.resolution || "",
    is_global: entry?.is_global || false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit() {
    if (
      !form.title.trim() ||
      !form.issue_type.trim() ||
      !form.description.trim() ||
      !form.root_cause.trim() ||
      !form.resolution.trim()
    ) {
      setError("All fields marked * are required.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let saved;
      if (isEdit) {
        saved = await updateEntry(entry.id, {
          title: form.title,
          issue_type: form.issue_type,
          description: form.description,
          root_cause: form.root_cause,
          resolution: form.resolution,
        });
      } else {
        saved = await createEntry(form);
      }
      onSaved(saved, isEdit);
      onClose();
    } catch (e) {
      setError(`Failed to ${isEdit ? "update" : "create"} entry: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }

  const fields = [
    { key: "title", label: "Title", placeholder: "Short descriptive title" },
    {
      key: "issue_type",
      label: "Issue type",
      placeholder: "e.g. disk_full, oom_kill, network_timeout",
    },
    {
      key: "description",
      label: "Description",
      textarea: true,
      placeholder: "Describe when this issue occurs…",
    },
    {
      key: "root_cause",
      label: "Root cause",
      textarea: true,
      placeholder: "What causes this issue?",
    },
    {
      key: "resolution",
      label: "Resolution",
      textarea: true,
      placeholder: "Step-by-step fix…",
    },
  ];

  return (
    <Modal
      title={isEdit ? "Edit KB Entry" : "New KB Entry"}
      onClose={onClose}
      width={580}
    >
      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#FEF2F2",
            color: "#DC2626",
            padding: "10px 12px",
            borderRadius: 8,
            fontSize: 12,
            marginBottom: 14,
            border: "1px solid #FECACA",
          }}
        >
          <Icon name="alert" size={14} color="#DC2626" />
          {error}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {fields.map((f) => (
          <div key={f.key}>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 4,
              }}
            >
              {f.label} <span style={{ color: "#DC2626" }}>*</span>
            </label>
            {f.textarea ? (
              <textarea
                value={form[f.key]}
                onChange={(e) =>
                  setForm((p) => ({ ...p, [f.key]: e.target.value }))
                }
                placeholder={f.placeholder}
                rows={3}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  border: "1px solid #E5E7EB",
                  borderRadius: 7,
                  fontSize: 12,
                  boxSizing: "border-box",
                  resize: "vertical",
                  outline: "none",
                  fontFamily: "inherit",
                  lineHeight: 1.5,
                }}
              />
            ) : (
              <input
                value={form[f.key]}
                onChange={(e) =>
                  setForm((p) => ({ ...p, [f.key]: e.target.value }))
                }
                placeholder={f.placeholder}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  border: "1px solid #E5E7EB",
                  borderRadius: 7,
                  fontSize: 12,
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />
            )}
          </div>
        ))}
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            padding: "10px 12px",
            background: "#F8FAFC",
            borderRadius: 7,
            border: "1px solid #E2E8F0",
          }}
        >
          <input
            type="checkbox"
            checked={form.is_global}
            onChange={(e) =>
              setForm((p) => ({ ...p, is_global: e.target.checked }))
            }
            style={{ width: 14, height: 14 }}
          />
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
              Mark as global
            </div>
            <div style={{ fontSize: 11, color: "#9CA3AF" }}>
              Shared across all organizations
            </div>
          </div>
        </label>
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "flex-end",
          marginTop: 18,
        }}
      >
        <button
          onClick={onClose}
          style={{
            padding: "8px 16px",
            border: "1px solid #E5E7EB",
            borderRadius: 7,
            background: "#fff",
            fontSize: 12,
            cursor: "pointer",
            color: "#374151",
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            padding: "8px 18px",
            background: loading ? "#93C5FD" : "#1D4ED8",
            color: "#fff",
            border: "none",
            borderRadius: 7,
            fontSize: 12,
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Saving…" : isEdit ? "Save changes" : "Add entry"}
        </button>
      </div>
    </Modal>
  );
}

export default KBModal;
